import { useState } from "react";
import { FileText, MessageCircle, Phone, Send, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/utils/api";

const faqItems = [
  {
    id: "urgence",
    question: "Intervenez-vous en urgence 24h/24 ?",
    answer: "Oui, nos équipes interviennent en urgence sur Maisons-Alfort et tout le Val-de-Marne.",
  },
  {
    id: "devis",
    question: "Proposez-vous un devis gratuit ?",
    answer: "Nous établissons un devis clair et transparent avant toute intervention.",
  },
  {
    id: "services",
    question: "Quels services proposez-vous ?",
    answer: "Rideaux métalliques, vitrages, portes blindées, menuiserie aluminium, automatismes et métallerie.",
  },
];

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(faqItems[0]);
  const [formData, setFormData] = useState({ nom: "", telephone: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.telephone || !formData.email) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      await api.post("/api/leads", {
        source_page: "chatbot",
        type_demande: "devis",
        service: "Chatbot FAQ",
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email,
        description: formData.message || "Demande via chatbot FAQ",
        consentement: true,
      });
      setStatus("sent");
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="fixed bottom-24 left-6 z-40" data-testid="chatbot-widget">
      {open && (
        <div className="w-[320px] bg-white border border-brand-blue/15 rounded-sm shadow-xl" data-testid="chatbot-panel">
          <div className="flex items-center justify-between bg-brand-blue text-white px-4 py-3">
            <div>
              <p className="text-xs uppercase" data-testid="chatbot-title">
                Assistance Fast Alu
              </p>
              <p className="text-xs" data-testid="chatbot-subtitle">
                Réponse rapide + demande de devis
              </p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close">
              <X size={16} />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2" data-testid="chatbot-faq">
              {faqItems.map((item) => (
                <button
                  key={item.id}
                  className={`w-full text-left text-xs uppercase font-semibold px-3 py-2 rounded-sm border ${
                    selectedFaq.id === item.id
                      ? "border-brand-red bg-brand-red/10"
                      : "border-brand-blue/15"
                  }`}
                  onClick={() => setSelectedFaq(item)}
                  data-testid={`chatbot-faq-${item.id}`}
                >
                  {item.question}
                </button>
              ))}
              <p className="text-xs text-brand-ink/70" data-testid="chatbot-faq-answer">
                {selectedFaq.answer}
              </p>
            </div>

            <div className="space-y-2" data-testid="chatbot-form">
              <input
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 text-sm"
                placeholder="Nom"
                value={formData.nom}
                onChange={handleChange("nom")}
                data-testid="chatbot-name"
              />
              <input
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 text-sm"
                placeholder="Téléphone"
                value={formData.telephone}
                onChange={handleChange("telephone")}
                data-testid="chatbot-phone"
              />
              <input
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 text-sm"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange("email")}
                data-testid="chatbot-email"
              />
              <textarea
                className="w-full border border-brand-blue/15 rounded-sm px-3 py-2 text-sm min-h-[80px]"
                placeholder="Votre demande"
                value={formData.message}
                onChange={handleChange("message")}
                data-testid="chatbot-message"
              />
              <button
                onClick={handleSubmit}
                className="w-full bg-brand-red text-white rounded-full py-2 text-xs uppercase font-semibold flex items-center justify-center gap-2"
                data-testid="chatbot-submit"
              >
                <Send size={14} />
                {status === "sending" ? "Envoi..." : "Envoyer"}
              </button>
              {status === "sent" && (
                <p className="text-xs text-brand-blue" data-testid="chatbot-success">
                  Merci ! Nous vous recontactons rapidement.
                </p>
              )}
              {status === "error" && (
                <p className="text-xs text-brand-red" data-testid="chatbot-error">
                  Merci de renseigner vos coordonnées.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <a
                href="tel:0184160307"
                className="flex-1 border border-brand-blue/40 rounded-full py-2 text-xs uppercase font-semibold flex items-center justify-center gap-2"
                data-testid="chatbot-call"
              >
                <Phone size={14} />
                Appelez
              </a>
              <Link
                to="/contact"
                className="flex-1 bg-brand-blue text-white rounded-full py-2 text-xs uppercase font-semibold flex items-center justify-center gap-2"
                data-testid="chatbot-quote"
              >
                <FileText size={14} />
                Devis
              </Link>
            </div>
          </div>
        </div>
      )}

      <button
        className="bg-brand-red text-white rounded-full px-4 py-3 flex items-center gap-2 shadow-lg"
        onClick={() => setOpen((prev) => !prev)}
        data-testid="chatbot-toggle"
      >
        <MessageCircle size={18} />
        Aide rapide
      </button>
    </div>
  );
};

export default ChatbotWidget;
